/**
 * CREATIVE DETONATOR - Storyboard API
 * API calls for video storyboard generation
 */

import { API_CONFIG } from '../core/state.js';
import {
  setStoryboard,
  getStoryboard,
  setGenerationStatus,
  setFrameStatus,
  setSceneFrame,
  saveToDatabase,
  isDatabaseIdea
} from './state.js';

// ============================================================================
// SCENE DETECTION HELPERS
// ============================================================================

/**
 * Detect scene count from idea - checks stored data first, then parses text
 * Used for UI updates before backend call
 * @param {string} text - The idea text to analyze
 * @param {Object} idea - Optional idea object with stored sceneCount/structure
 * @returns {number|null} Detected scene count or null
 */
export function detectSceneCountFromText(text, idea = null) {
  // Priority 1: Check stored sceneCount (from generation or database)
  if (idea?.sceneCount && idea.sceneCount > 0) {
    return idea.sceneCount;
  }

  // Priority 2: Check structure.scenes array
  if (idea?.structure?.scenes?.length > 0) {
    return idea.structure.scenes.length;
  }

  // Priority 3: Check meta.sceneCount (from database)
  if (idea?.meta?.sceneCount && idea.meta.sceneCount > 0) {
    return idea.meta.sceneCount;
  }

  // Priority 4: Check meta.scenes array (from database)
  if (idea?.meta?.scenes?.length > 0) {
    return idea.meta.scenes.length;
  }

  // Fallback: Parse from text
  if (!text) return null;

  // Pattern 1: Numbered shots like "1.", "2."
  const numberedShots = text.match(/^[\s]*(\d+)[\.\):\s]/gm);
  if (numberedShots && numberedShots.length >= 2) {
    return numberedShots.length;
  }

  // Pattern 2: Timing brackets like "[0s-15s]"
  const timingBrackets = text.match(/\[\d+s?-\d+s?\]/gi);
  if (timingBrackets && timingBrackets.length >= 2) {
    return timingBrackets.length;
  }

  // Pattern 3: SHOT/SCENE markers
  const shotMarkers = text.match(/\b(SHOT|SCENE)\s*\d+/gi);
  if (shotMarkers && shotMarkers.length >= 2) {
    return shotMarkers.length;
  }

  // Pattern 4: Duration-based heuristic
  const durationMatch = text.match(/:?(\d+)\s*(?:seconds?|s\b)/i);
  if (durationMatch) {
    const seconds = parseInt(durationMatch[1], 10);
    if (seconds <= 15) return 3;
    if (seconds <= 30) return 5;
    if (seconds <= 45) return 7;
    return 8;
  }

  return null; // Unknown - let backend/AI decide
}

// ============================================================================
// SCENE GENERATION
// ============================================================================

/**
 * Generate video scenes for an idea
 * @param {Object} idea - The idea object
 * @param {Object} brief - The brief (product, benefit)
 * @param {Object} options - Generation options
 * @returns {Promise<Object>} Generated storyboard
 */
export async function generateScenes(idea, brief, options = {}) {
  const ideaId = idea.id;

  console.log('[Storyboard API] Starting generation for:', ideaId);
  console.log('[Storyboard API] Brief:', brief);

  // Set generating status
  setGenerationStatus(ideaId, 'generating');

  try {
    console.log('[Storyboard API] Calling /api/generate-video-scenes...');
    const response = await fetch(`${API_CONFIG.baseUrl}/api/generate-video-scenes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idea,
        brief,
        options: {
          sceneCount: options.sceneCount || 3,
          duration: options.duration || 30,
          independent: true // Request independent scenes
        }
      })
    });

    console.log('[Storyboard API] Response status:', response.status);
    const result = await response.json();
    console.log('[Storyboard API] Response:', result.success ? 'SUCCESS' : 'FAILED', result);

    if (!result.success) {
      throw new Error(result.error || 'Failed to generate scenes');
    }

    // Transform to new format
    console.log('[Storyboard API] Transforming response...');
    const storyboard = transformToStoryboard(result.data, idea);
    console.log('[Storyboard API] Storyboard created with', storyboard.scenes?.length, 'scenes');

    // Store in state
    setStoryboard(ideaId, storyboard);

    // Save to database if applicable
    if (isDatabaseIdea(ideaId)) {
      console.log('[Storyboard API] Saving to database...');
      await saveToDatabase(ideaId);
    }

    setGenerationStatus(ideaId, 'complete');
    return storyboard;
  } catch (error) {
    setGenerationStatus(ideaId, 'error');
    console.error('[Storyboard API] Generation failed:', error);
    throw error;
  }
}

/**
 * Transform API response to new storyboard format
 * @param {Object} data - API response data
 * @param {Object} idea - Source idea
 * @returns {Object} Storyboard object
 */
function transformToStoryboard(data, idea) {
  return {
    version: 1,
    generatedAt: new Date().toISOString(),
    regeneratedAt: null,

    // Style
    style: {
      visual: data.style || 'Cinematic commercial',
      colorGrade: 'Natural',
      aspectRatio: '16:9'
    },

    // Duration
    duration: data.duration || ':30',

    // Scenes
    scenes: (data.scenes || []).map((scene, index) => ({
      id: `scene_${scene.seq || index + 1}`,
      seq: scene.seq || index + 1,
      timing: scene.timing || `${index * 10}s-${(index + 1) * 10}s`,

      // Visual prompt (the main text for video AI)
      visualPrompt: buildVisualPrompt(scene, data.style),
      visual: scene.visual,

      // Structured metadata
      setting: scene.setting || extractSetting(scene.visual),
      subject: scene.subject || '',
      action: scene.action || '',
      mood: scene.mood || '',

      // Camera - keep as string for simplicity
      camera: typeof scene.camera === 'string' ? scene.camera :
              (scene.camera?.movement || scene.camera?.shot || ''),

      // Voiceover - spoken narration (separate from SFX/music)
      voiceover: (scene.voiceover && scene.voiceover.toLowerCase() !== 'null') ? scene.voiceover : null,

      // Audio - SFX/music only (NOT voiceover)
      audio: typeof scene.audio === 'string' ? scene.audio :
             (scene.audio?.sfx || scene.audio?.music || ''),

      // Frames (to be generated)
      frames: {
        start: null,
        end: null
      },

      // Video (future)
      video: {
        url: null,
        status: 'pending',
        provider: null
      },

      // Edit state
      isEdited: false,
      originalPrompt: null
    })),

    // End frame
    endFrame: {
      description: data.endFrame || '',
      duration: '3s',
      image: null
    },

    // Source reference
    sourceIdea: {
      id: idea.id,
      headline: idea.headline || idea.title,
      visual: idea.visual || idea.text
    }
  };
}

/**
 * Build complete visual prompt for video AI
 * @param {Object} scene
 * @param {string} style
 * @returns {string}
 */
function buildVisualPrompt(scene, style) {
  let prompt = '';

  // Add style prefix
  if (style) {
    prompt += `${style}. `;
  }

  // Add main visual
  if (scene.visual) {
    prompt += scene.visual;
  }

  // Add camera if present
  if (scene.camera) {
    const cameraText = typeof scene.camera === 'object'
      ? `${scene.camera.shot} ${scene.camera.movement}`.trim()
      : scene.camera;
    if (cameraText) {
      prompt += ` ${cameraText}.`;
    }
  }

  return prompt.trim();
}

/**
 * Extract setting from visual description (simple heuristic)
 * @param {string} visual
 * @returns {string}
 */
function extractSetting(visual) {
  if (!visual) return '';

  // Look for common setting indicators
  const settingPatterns = [
    /^(Interior|Exterior)\s*[,.]?\s*/i,
    /^(In|At|On)\s+(?:a|an|the)\s+\w+/i
  ];

  for (const pattern of settingPatterns) {
    const match = visual.match(pattern);
    if (match) {
      // Extract first sentence as setting
      const firstSentence = visual.split(/[.!]/)[0];
      return firstSentence.substring(0, 100);
    }
  }

  return '';
}

// ============================================================================
// FRAME IMAGE GENERATION
// ============================================================================

/**
 * Generate a frame image for a scene
 * @param {Object} scene - The scene object
 * @param {'start'|'end'} frameType - Which frame to generate
 * @param {Object} brief - The brief (product, benefit)
 * @param {Object} options - Generation options
 * @returns {Promise<string>} Image URL
 */
export async function generateSceneFrame(scene, frameType, brief, options = {}) {
  const sceneSeq = scene.seq;

  try {
    // Build image prompt with brief context
    const prompt = buildFramePrompt(scene, frameType, brief, options);

    // Call scene frame generation API
    const response = await fetch(`${API_CONFIG.baseUrl}/api/generate-scene-frame`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sceneSeq,
        frameType,
        prompt,
        brief,
        provider: options.provider || 'seedream',
        aspectRatio: options.aspectRatio || '16:9',
        style: options.style || 'advertising'
      })
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Image generation failed');
    }

    const imageUrl = result.imageUrl;

    // Update scene with image URL
    setSceneFrame(sceneSeq, frameType, imageUrl);

    return imageUrl;
  } catch (error) {
    console.error('[Storyboard API] Frame generation failed:', error);
    throw error;
  }
}

/**
 * Build image prompt for frame generation
 * @param {Object} scene
 * @param {'start'|'end'} frameType
 * @param {Object} brief - Product/benefit context
 * @param {Object} options
 * @returns {string}
 */
function buildFramePrompt(scene, frameType, brief = {}, options = {}) {
  let prompt = 'Professional advertising photography. ';

  // Add visual description
  if (scene.visual) {
    prompt += scene.visual;
  }

  // Add frame-specific context
  if (frameType === 'start') {
    prompt += ' Beginning of the scene, establishing shot.';
  } else if (frameType === 'end') {
    prompt += ' End of the scene, resolution moment.';
  }

  // Add camera framing
  if (scene.camera) {
    const cameraText = typeof scene.camera === 'object'
      ? scene.camera.shot || scene.camera.movement
      : scene.camera;
    if (cameraText) {
      prompt += ` Camera: ${cameraText}.`;
    }
  }

  // Add mood
  if (scene.mood) {
    prompt += ` Mood: ${scene.mood}.`;
  }

  // Add product context if available
  if (brief.product) {
    prompt += ` Product featured: ${brief.product}.`;
  }

  return prompt.trim();
}

// ============================================================================
// STORYBOARD GRID SPLITTING
// ============================================================================

/**
 * Split a storyboard grid image into individual panels
 * Uses the grid splitter API to slice a 2x3 (or custom) grid into separate panels
 *
 * Backend uploads panels to R2 and returns URLs (not Base64) to avoid sessionStorage limits.
 *
 * @param {string} imageUrl - URL or data URL of the grid image
 * @param {number} rows - Number of rows in the grid (default 2)
 * @param {number} cols - Number of columns in the grid (default 3)
 * @param {Object} options - Additional options
 * @returns {Promise<Array>} Array of panel objects with url (R2) or dataUrl (fallback), index, row, col
 */
export async function splitStoryboardGrid(imageUrl, rows = 2, cols = 3, options = {}) {
  console.log('[Storyboard API] Splitting grid:', { imageUrl: imageUrl?.substring(0, 50), rows, cols });

  try {
    const response = await fetch(`${API_CONFIG.baseUrl}/api/storyboard/split`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        imageUrl: imageUrl,
        rows: rows,
        cols: cols,
        trimBorder: options.trimBorder !== false, // Default true
        refineEdges: !!options.refineEdges // Default false - only enable when explicitly requested
      })
    });

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Split failed');
    }

    const storageType = result.data.meta?.storage || 'unknown';
    console.log('[Storyboard API] Split complete:', result.data.panelCount, 'panels, storage:', storageType);

    // Normalize panels: ensure each has a displayable URL
    // Backend returns 'url' for R2 storage, 'dataUrl' for Base64 fallback
    const normalizedPanels = result.data.panels.map(panel => ({
      ...panel,
      // Prefer url (R2), fallback to dataUrl (Base64)
      // Store the displayable URL in dataUrl for backward compatibility
      dataUrl: panel.url || panel.dataUrl,
      // Keep original url and path for R2 cleanup
      url: panel.url || null,
      path: panel.path || null,
      source: panel.source || 'split'
    }));

    return normalizedPanels;
  } catch (error) {
    console.error('[Storyboard API] Grid split failed:', error);
    throw error;
  }
}

// ============================================================================
// EXPORT
// ============================================================================

export {
  buildVisualPrompt,
  buildFramePrompt
};
